export enum ApiErrorKind {
    Network = 'network',
    Timeout = 'timeout',
    Unauthorized = 'unauthorized', // 401
    Forbidden = 'forbidden', // 403
    NotFound = 'notFound', // 404
    Validation = 'validation', // 400
    Conflict = 'conflict', // 409
    Server = 'server', // 500
    Unknown = 'unknown',
}

const RETRYABLE: ReadonlySet<ApiErrorKind> = new Set([
    ApiErrorKind.Network,
    ApiErrorKind.Timeout,
    ApiErrorKind.Server,
]);

export function defaultMessage(kind: ApiErrorKind): string {
    switch (kind) {
        case ApiErrorKind.Network:
            return "We couldn't reach the server. Check your connection and try again.";
        case ApiErrorKind.Timeout:
            return 'The server took too long to respond. Please try again.';
        case ApiErrorKind.Unauthorized:
            return 'Your session has expired. Please sign in again.';
        case ApiErrorKind.Forbidden:
            return "You don't have permission to make this change.";
        case ApiErrorKind.NotFound:
            return 'This record no longer exists — it may have been deleted.';
        case ApiErrorKind.Validation:
            return 'Some of the information entered could not be saved.';
        case ApiErrorKind.Conflict:
            return 'Someone else changed this record while you were editing it.';
        case ApiErrorKind.Server:
            return 'Something went wrong on our end. Please try again.';
        case ApiErrorKind.Unknown:
            return 'Something went wrong. Please try again.';
    }
}

interface ApiErrorDetail {
    status?: number | null;
    messages?: string[];
    fieldErrors?: Record<string, string[]>;
}

export class BusinessProfileApiError extends Error {
    readonly kind: ApiErrorKind;
    readonly status: number | null;
    readonly messages: string[];
    readonly fieldErrors: Record<string, string[]>;

    constructor(kind: ApiErrorKind, detail: ApiErrorDetail = {}) {
        const messages = detail.messages?.length ? detail.messages : [defaultMessage(kind)];
        super(messages[0]);

        this.name = 'BusinessProfileApiError';
        this.kind = kind;
        this.status = detail.status ?? null;
        this.messages = messages;
        this.fieldErrors = detail.fieldErrors ?? {};
    }

    get isRetryable(): boolean {
        return RETRYABLE.has(this.kind);
    }

    get hasFieldErrors(): boolean {
        return Object.keys(this.fieldErrors).length > 0;
    }
}

export function toApiError(error: unknown): BusinessProfileApiError {
    if (error instanceof BusinessProfileApiError) {
        return error;
    }
    return new BusinessProfileApiError(ApiErrorKind.Unknown, {
        messages: error instanceof Error && error.message ? [error.message] : undefined,
    });
}

export const isRetryableError = (error: unknown): boolean => toApiError(error).isRetryable;
